/**
 * Dialogue data for 绿色码头 (P9–P11 of docs/STORY_ADAPTATION.md).
 *
 * FIDELITY RULES applied here:
 *  - every quoted line from story.txt is verbatim (only the documented typo normalisations:
 *    不然处 → 不远处, 有我们这些…发现 → 由我们这些…发现; plus one undocumented 衍字 in P11 narration,
 *    "勘察南陆地理的的各种动物" → "勘察南陆地理的各种动物", listed in the builder report);
 *  - original narration is kept as narration nodes (no speaker) so nothing is put in a mouth it
 *    did not come from;
 *  - environment NPC lines only characterise people and place; multilingual flavour is readable
 *    Chinese with a bracketed note, never gibberish; nothing explains 旱龟 / 黑水 / 预言 / 龟壳.
 */
import type { DialogueData, DialogueNode } from '@/core/types/contracts';
import { ACTOR, ghNpcId } from './actors';

const J = ACTOR.JIANG;
const Z = ACTOR.ZHAO;
const M = ACTOR.MERCHANT;
/** Environment NPC speaker id (`candle-seller` → `gh-candle-seller`). */
const N = ghNpcId;

/** Build a linear dialogue from an ordered node list (ids auto-chained). */
function linear(id: string, nodes: Omit<DialogueNode, 'id' | 'next'>[], opts: { setFlag?: string; setMark?: string } = {}): DialogueData {
  const out: DialogueNode[] = nodes.map((n, i) => ({ id: `${i + 1}`, ...n, next: i + 1 < nodes.length ? `${i + 2}` : undefined }));
  const last = out[out.length - 1];
  if (last && opts.setFlag) last.setFlag = opts.setFlag;
  if (last && opts.setMark) last.setMark = opts.setMark;
  return { id, start: '1', nodes: out };
}
const say = (speaker: string, text: string, extra: Partial<DialogueNode> = {}): Omit<DialogueNode, 'id' | 'next'> => ({ speaker, text, ...extra });
const narr = (text: string, extra: Partial<DialogueNode> = {}): Omit<DialogueNode, 'id' | 'next'> => ({ text, ...extra });

// ------------------------------------------------------------------ main line: P9 opening
export const DLG_OPENING = linear('gh.opening', [
  narr('不知何时，江子潮来到身边，望着赵呈博。'),
  say(J, '老师，你在想什么呢？', { emotion: 'curious', face: { actor: Z, facing: 'left' } }),
  narr('一路上赵呈博教了江子潮不少东西，他们之间不知不觉间以师徒相称。'),
  say(Z, '没想什么，走吧，我们要尽早找到旱龟蛋。', { emotion: 'calm' }),
]);

// ------------------------------------------------------------------ P9 walking commentary beats
export const DLG_BEAT_PIER = linear('gh.beat.pier', [
  narr('绿色码头是位于南陆最北的码头，是通向中原大陆最近的码头。'),
  narr('也许是太热的原因，这里的人大多皮肤颜色较深，身材短小，说的语言也很奇怪；也有一些身材高大的人，皮肤白皙。'),
  narr('还好有赵呈博，这位尊敬的长者显然通晓这里的语言，江子潮也能不受限制的了解这里的人们。'),
  narr('这里是生意人码头，主要是与中原大陆之间的生意互通有无。'),
], { setMark: 'beat:pier' });

export const DLG_BEAT_CANDLE = linear('gh.beat.candle', [
  say(N('candle-seller'), '蜡烛！中原来的好蜡烛！一金币一截！', { emotion: 'shout', emote: { actor: N('candle-seller'), emote: 'coin' } }),
  narr('中原常见的蜡烛，在这里居然卖到了一个金币一截，也算是奸商的天价了。'),
  narr('怪不得船长听说他们都是彻夜点蜡烛如此的惊诧。', { emote: { actor: J, emote: 'sweat' } }),
], { setMark: 'beat:candle' });

export const DLG_BEAT_MARKET = linear('gh.beat.market', [
  say(Z, '你注意到没，', { emotion: 'calm', face: { actor: J, facing: 'left' } }),
  say(Z, '这里做生意做得红火的人都是这些棕色皮肤、身材矮小的人。'),
  narr('江子潮望去，果然，在大片的树叶下面，摆摊面积比较大的，后面的叫卖的都是棕色皮肤、身材矮小的青年人。', { face: { actor: J, facing: 'right' } }),
  narr('虽然听不懂他们叫卖什么，但看看他们快活的表情，面前奇怪而丰富的货物，就知道他们的生意做得很不错。'),
], { setMark: 'beat:market' });

export const DLG_BEAT_LEAVES = linear('gh.beat.leaves', [
  say(Z, '这些人不是最早的南陆人。他们大多数是中原北方生意人的后裔。这些生意人开始把北方的特产往中原带，后来走不动了，就在当地扎根，成家生子。他们的后代继续他们的足迹，往南行进。', { emotion: 'calm' }),
  say(Z, '中原的版图，由我们这些地理勘探者发现，更多的是由这些生意人走出来的。以前的时候没有路，他们几十年或者好几代人才能迁徙几百里。'),
  say(Z, '最近的几百年里，你也知道的，马车，牛车，以及鲁先生发明的一些动力载具，他们的生意做到了南陆。他们喜欢称自己为亚北人。'),
  narr('赵呈博说道。对于风土人情，他确实了解很多。'),
], { setMark: 'beat:leaves' });

// ------------------------------------------------------------------ P10 亚北商贩 (full, comedic pacing)
export const DLG_MERCHANT = linear('gh.merchant', [
  narr('江子潮听得入神，看着不远处一棵棕榈树下快活的年轻人，忍不住向他打招呼：', { face: { actor: J, facing: 'up' } }),
  say(J, '嗨！', { emotion: 'bright', emote: { actor: J, emote: 'wave' } }),
  narr('那个年轻人灵活的眼珠转了转，很快回答道：', { emote: { actor: M, emote: 'blink' } }),
  say(M, '嗨，哥们！要点啥？', { emotion: 'grin' }),
  say(J, '你会说中原话？', { emotion: 'surprised', emote: { actor: J, emote: '?' } }),
  say(M, '那当然。我们这些做生意的，基本上都会说三种以上的语言呢。', { emotion: 'grin', emote: { actor: M, emote: 'laugh' } }),
  narr('年轻人笑嘻嘻的说。他当然没有忘记他的本行：'),
  say(M, '看，要点什么带回中原？比如这颗仙人球？保证你没看过，放在家里四季常青！', { emotion: 'sell', emote: { actor: M, emote: 'cactus' } }),
  say(M, '还有这个袋鼠皮！中原人最喜欢带这个回去了，老实说这东西在这里都没人要……', { emotion: 'sell' }),
  say(M, '哦，我不能说这个...客户想要的就是我们提供的！', { emotion: 'sweat', emote: { actor: M, emote: 'sweat' } }),
  narr('年轻人絮絮叨叨说了许多，一点也没有累的样子。江子潮倒是不好意思的准备转身离开。', { face: { actor: J, facing: 'down' }, emote: { actor: J, emote: 'sweat' } }),
  say(M, '还有这个，圆圆的，大大的蛋，看见没？你知道是什么吗？这可是正宗的鸵鸟蛋……', { emotion: 'sell', emote: { actor: M, emote: 'egg' } }),
  say(M, '买回去干什么都行...做成化石摆在家里，孵化小鸵鸟，或者找个好点儿的厨子做来吃了...', { emotion: 'grin' }),
  say(J, '……那么，你这里有旱龟蛋吗？！', { emotion: 'urgent', face: { actor: J, facing: 'up' }, emote: { actor: J, emote: '!' } }),
  narr('听到“蛋”，江子潮立刻想起了此行的目的。'),
  say(M, '哦，海龟蛋？哦，现在可不是季节啊。。。还得三个月他们才会上岸下蛋呢……', { emotion: 'think', emote: { actor: M, emote: 'think' } }),
  narr('年轻人突然压低了声音：', { emote: { actor: M, emote: 'shh' }, sfx: 'ui-hush' }),
  say(M, '再说，海龟蛋在这里是禁止交易的。。。前面那个旅馆，看见了吗？名字就是大海龟旅店，你要买海龟蛋，这事咱们得私下谈……', { emotion: 'whisper', face: { actor: M, facing: 'right' } }),
  say(J, '不，不是海龟蛋，', { emotion: 'serious', face: { actor: M, facing: 'down' } }),
  narr('江子潮认真的发音，以免这个热情的亚北人听错。'),
  say(J, '是旱龟蛋。旱──龟──蛋，你有吗？', { emotion: 'serious' }),
  say(M, '旱龟蛋？旱龟？从来没听说！哪有旱龟，没有水哪来的乌龟啊！你是开玩笑吧老兄？', { emotion: 'puzzled', emote: { actor: M, emote: '?' } }),
  say(M, '我没见过，没听说过，我也没听我父亲说过，我爷爷也没说过，我爷爷的父亲也没说过……', { emotion: 'ramble', emote: { actor: M, emote: '...' } }),
  narr('江子潮有点受不了这个啰嗦的年轻人，摇摇头走开了。', { emote: { actor: J, emote: 'shake' } }),
]);

// ------------------------------------------------------------------ P11 雨将至
export const DLG_RAIN_WALK = linear('gh.rain.walk', [
  narr('顺着熙熙攘攘的人群，赵呈博慢慢的走着，边走边看着天。'),
  narr('他很清楚江子潮不会从亚北人小贩那里买到旱龟蛋的──如果真的那么容易的话，万千梅巫师也不会让他来帮忙。'),
  narr('他仔细的回忆着在过去勘察南陆地理的各种动物，依然没有旱龟的头绪。'),
  narr('江子潮费力的跟了上来。还好赵呈博白发白须，在人群中很容易辨认。'),
]);
export const DLG_RAIN_TALK = linear('gh.rain.talk', [
  say(J, '老师，我们去哪里找旱龟呢？', { emotion: 'tired', face: { actor: Z, facing: 'left' } }),
  say(Z, '我们先去找个地方歇歇，顺便问问那里的人。一会儿就下雨了。', { emotion: 'calm', face: { actor: Z, facing: 'up' } }),
]);
/** Soft guidance once the drizzle starts (narration from the inn paragraph; describes NPC behaviour the player can see). */
export const DLG_RAIN_HINT = linear('gh.rain.hint', [
  narr('这里的人大多数了解这里的气候，眼看不会下多大的雨，年轻一点的就满不在乎的继续做自己的事，女孩子们和年长一些的有些站在大海龟旅店门口，有些则进去趁机喝一杯。'),
], { setMark: 'seen:rain-hint' });

// ------------------------------------------------------------------ observation points (flavour only)
const obs = (id: string, lines: string[], mark?: string): DialogueData => linear(id, lines.map((t) => narr(t)), { setMark: mark });
export const DLG_OBSERVATIONS: DialogueData[] = [
  obs('gh.obs.foreign-ship', ['一艘异邦的船。船身又高又白，船头翘得很厉害，跟中原的帆船完全不是一个样子。', '甲板上的人个子都很高。'], 'seen:foreign-ship'),
  obs('gh.obs.pier-end', ['海面一直铺到天边。来南陆的路，就是从那个方向过来的。', '给琼的信已经交给随船返回的人了。'], 'seen:pier-end'),
  obs('gh.obs.junk', ['一艘搁浅的旧帆船，半个船身陷在沙里。有人在船底敲敲打打。', '船帆早就卸下来了，桅杆上晾着渔网。'], 'seen:junk'),
  obs('gh.obs.net', ['晒着的渔网，网眼很大，还挂着几片没抖干净的海草。'], 'seen:net'),
  obs('gh.obs.crane', ['木头搭的起重架，绞盘上缠着粗麻绳。刚从船上吊下来的货箱还在晃。'], 'seen:crane'),
  obs('gh.obs.crates', ['一摞摞的货箱，箱面上烙着看不懂的记号。有一箱没盖严，露出一角深色的布。'], 'seen:crates'),
  obs('gh.obs.jars', ['封着口的陶罐，一排排晒在太阳底下。凑近能闻到香料的味道。'], 'seen:jars'),
  obs('gh.obs.shed', ['码头的货棚，棚顶是一层层的大叶子铺成的。棚里堆着等船的货。'], 'seen:shed'),
  obs('gh.obs.candles', ['摊上摆着几截蜡烛，被小心地用布垫着，像什么稀罕物件。', '在星塘，这样一截蜡烛也就换几个铜钱。'], 'seen:candles'),
  obs('gh.obs.spices', ['一小袋一小袋的香料，颜色从土黄到深红。摊主看见有人看，立刻笑着递过来一撮让人闻。'], 'seen:spices'),
  obs('gh.obs.cloth', ['中原来的布匹，颜色朴素。琼织的布比这个细密多了。'], 'seen:cloth'),
  obs('gh.obs.fruit', ['奇怪的果子：有的长着毛，有的像拳头一样红，还有一种切开是紫色的。', '江子潮一个也叫不出名字。'], 'seen:fruit'),
  obs('gh.obs.fish', ['木盆里的鱼颜色鲜亮得不像能吃的东西。摊主说这是今早从礁石那边打的。'], 'seen:fish'),
  obs('gh.obs.big-jars', ['比人还高的大陶罐，罐口用蜡封着。摊后的亚北青年吆喝得很起劲。'], 'seen:big-jars'),
  obs('gh.obs.cactus', ['一颗圆滚滚、浑身是刺的绿球，种在一个小陶盆里。', '摊主说它放在家里四季常青。'], 'seen:cactus'),
  obs('gh.obs.ostrich-egg', ['一颗圆圆的、大大的蛋，比江子潮的拳头还大两圈。', '摊主说这是正宗的鸵鸟蛋。'], 'seen:ostrich-egg'),
  obs('gh.obs.kangaroo-pelt', ['挂在棕榈树干上的一张兽皮，毛色灰褐。摊主说中原人最喜欢带这个回去。'], 'seen:kangaroo-pelt'),
  obs('gh.obs.big-leaf', ['头顶是一大片一大片的树叶，一片就能盖住半个摊子。叶子底下比外面凉快得多。'], 'seen:big-leaf'),
  obs('gh.obs.lantern', ['灯笼杆上挂着的不是蜡烛，是一小盏油灯。天黑以后大概就靠它了。'], 'seen:lantern'),
  obs('gh.obs.hut', ['架在木桩上的吊脚楼，地板离地有半人高。大概是怕潮，也怕涨水。'], 'seen:hut'),
  obs('gh.obs.small-turtle', ['门口摆着的一只小海龟，是石头雕的，壳上的纹路磨得发亮。', '很多人进门前都会顺手摸一下它的头。'], 'seen:small-turtle'),
  obs('gh.obs.inn-sign', ['招牌上是几种文字，中间那行中原字写着：大海龟旅店。'], 'seen:inn-sign'),
  obs('gh.obs.inn-far', ['远远就能看见大海龟旅店。他的特征很明显，从人群看过去，屋顶俨然就像一个巨大的龟壳，没有围墙，只用几根柱子支撑起来。看起来像门口的地方摆着两只小海龟。'], 'seen:inn-far'),
  obs('gh.obs.inn-door-early', ['店里传出喝酒的声音。江子潮回头看了看老师，赵呈博还在市集那边慢慢地走着。'], 'seen:inn-door-early'),
];

// ------------------------------------------------------------------ environment NPC talk
const talk = (id: string, nodes: Omit<DialogueNode, 'id' | 'next'>[]): DialogueData => linear(id, nodes, { setMark: `talked:${id.replace('gh.npc.', '')}` });
/** The merchant's flavour talk keeps his characters-builder id (`gh.npc.yabei-merchant`). */
const w = (states: DialogueData['nodes'][number]['condition']): Partial<DialogueNode> => ({ condition: states });

export const DLG_NPCS: DialogueData[] = [
  talk('gh.npc.yabei-merchant', [
    say(M, '哥们，想好了没？仙人球，四季常青！', { emotion: 'grin' }),
    say(M, '旱龟蛋我是真没听说过。我爷爷的父亲也没说过。', { emotion: 'ramble' }),
  ]),
  talk('gh.npc.candle-seller', [
    say(N('candle-seller'), '（亚北语）蜡烛，蜡烛！一金币一截，不还价！', { emotion: 'shout' }),
    say(N('candle-seller'), '（中原话）哦，中原来的？那你们那边……真的整夜整夜地点这个？', { emotion: 'surprised' }),
  ]),
  talk('gh.npc.spice-seller', [
    say(N('spice-seller'), '（亚北语）闻闻看，这一小袋能煮一个月的汤。', { emotion: 'calm' }),
    say(N('spice-seller'), '天太闷了，叶子都不动。', { emotion: 'sigh', ...w({ weather: ['sultry'] }) }),
    say(N('spice-seller'), '起风了，要下雨了。你们中原人不习惯吧？', { emotion: 'calm', ...w({ weather: ['wind', 'drizzle', 'rain'] }) }),
  ]),
  talk('gh.npc.cloth-seller', [
    say(N('cloth-seller'), '中原来的布，货真价实。这一船是从长阳城过来的。', { emotion: 'calm' }),
    say(N('cloth-seller'), '这里的人不太买布，他们更爱买蜡烛。', { emotion: 'sigh' }),
  ]),
  talk('gh.npc.fruit-seller', [
    say(N('fruit-seller'), '（本地话）尝尝！不甜不要钱！', { emotion: 'grin' }),
    say(N('fruit-seller'), '（中原话）……你们中原人总是先问这是什么。是果子，吃的！', { emotion: 'laugh' }),
  ]),
  talk('gh.npc.fish-seller', [
    say(N('fish-seller'), '（本地话）今早礁石那边打的。看这个颜色！', { emotion: 'calm' }),
    say(N('fish-seller'), '下点雨怕什么，鱼又不怕水。', { emotion: 'laugh', ...w({ weather: ['drizzle', 'rain'] }) }),
  ]),
  talk('gh.npc.jar-seller', [
    say(N('jar-seller'), '（亚北语）大罐子！装酒装水装香料，装什么都行！', { emotion: 'shout' }),
    say(N('jar-seller'), '（中原话）我们家三代人在这里摆摊了。生意好得很！', { emotion: 'grin' }),
  ]),
  talk('gh.npc.sailor-zhongyuan', [
    say(N('sailor-zhongyuan'), '还是船上凉快。这码头热得人发昏。', { emotion: 'tired' }),
    say(N('sailor-zhongyuan'), '你们也是坐我们那条船来的吧？船长还惦记着卖他那些香料呢。', { emotion: 'laugh' }),
  ]),
  talk('gh.npc.sailor-foreign', [
    say(N('sailor-foreign'), '（异邦语）绳子……绳子要收好。海上不会等人。', { emotion: 'calm' }),
    say(N('sailor-foreign'), '（生硬的中原话）你，中原？我们，北边更北。', { emotion: 'grin' }),
  ]),
  talk('gh.npc.porter-1', [
    say(N('porter-1'), '（本地话）让让，让让！箱子重！', { emotion: 'shout' }),
  ]),
  talk('gh.npc.porter-2', [
    say(N('porter-2'), '（本地话）这一船卸完就能歇了……还有三十箱。', { emotion: 'tired' }),
  ]),
  talk('gh.npc.crane-worker', [
    say(N('crane-worker'), '起重架是鲁先生的法子改的，一个人就能吊起两个人的货。', { emotion: 'calm' }),
    say(N('crane-worker'), '别站在货底下，绳子老了。', { emotion: 'serious' }),
  ]),
  talk('gh.npc.shipwright', [
    say(N('shipwright'), '（本地话）这条船搁在这里两年了。船主人说要修，年年说。', { emotion: 'calm' }),
    say(N('shipwright'), '我照样每天敲两下，习惯了。', { emotion: 'laugh' }),
  ]),
  talk('gh.npc.traveller', [
    say(N('traveller'), '在下周明，等回中原的船。已经等了七天了。', { emotion: 'tired' }),
    say(N('traveller'), '兄台也是从长阳城来的？那可真是……唉，这里的蜡烛价钱你听说了吗？', { emotion: 'sigh' }),
  ]),
  talk('gh.npc.pale-trader', [
    say(N('pale-trader'), '（异邦语）布料、香料、蜡烛。这里什么都贵，什么都有人买。', { emotion: 'calm' }),
    say(N('pale-trader'), '（中原话，口音很重）朋友，你从北边来？很远，很远。', { emotion: 'calm' }),
  ]),
  talk('gh.npc.girl-1', [
    say(N('girl-1'), '（本地话）你看那个白胡子的老爷爷，头发全白了！', { emotion: 'giggle' }),
    say(N('girl-1'), '要下雨了，我们去旅店门口躲一躲。', { emotion: 'giggle', ...w({ weather: ['wind', 'drizzle', 'rain'] }) }),
  ]),
  talk('gh.npc.girl-2', [
    say(N('girl-2'), '（本地话）小声点，他听得懂我们说话。', { emotion: 'shy' }),
  ]),
  talk('gh.npc.elder', [
    say(N('elder'), '（本地话）年轻人，坐下歇歇。这个时辰不歇，一会儿雨来了就走不动了。', { emotion: 'calm' }),
    say(N('elder'), '雨不大。年轻人不躲，我们老的躲。', { emotion: 'calm', ...w({ weather: ['drizzle', 'rain'] }) }),
  ]),
];

// ------------------------------------------------------------------ stall fronts: look at the goods, the vendor answers
/**
 * One prompt per vendor stall (maps/green-harbor.ts builds the `talk` rect from `HarborNpcDef.stall`):
 * the goods observation first, then the vendor's own lines — so the vendor lines are reachable from
 * the natural front-of-stall position (the counter is two rows deep, out of the dynamic talk reach).
 * Node marks (`seen:*`, `talked:*`) are kept, so both halves count as seen.
 */
const byId = (id: string): DialogueData => {
  const d = [...DLG_OBSERVATIONS, ...DLG_NPCS].find((x) => x.id === id);
  if (!d) throw new Error(`green-harbor: no dialogue ${id}`);
  return d;
};
const strip = (n: DialogueNode): Omit<DialogueNode, 'id' | 'next'> => { const { id: _id, next: _next, ...rest } = n; return rest; };
export const stallDialogue = (short: string, observe: string): DialogueData =>
  linear(`gh.stall.${short}`, [...byId(observe).nodes.map(strip), ...byId(`gh.npc.${short}`).nodes.map(strip)]);
export const DLG_STALLS: DialogueData[] = [
  stallDialogue('candle-seller', 'gh.obs.candles'),
  stallDialogue('spice-seller', 'gh.obs.spices'),
  stallDialogue('cloth-seller', 'gh.obs.cloth'),
  stallDialogue('fruit-seller', 'gh.obs.fruit'),
  stallDialogue('fish-seller', 'gh.obs.fish'),
  stallDialogue('jar-seller', 'gh.obs.big-jars'),
];

export const GREEN_HARBOR_DIALOGUES: DialogueData[] = [
  DLG_OPENING, DLG_BEAT_PIER, DLG_BEAT_CANDLE, DLG_BEAT_MARKET, DLG_BEAT_LEAVES,
  DLG_MERCHANT, DLG_RAIN_WALK, DLG_RAIN_TALK, DLG_RAIN_HINT,
  ...DLG_OBSERVATIONS, ...DLG_NPCS, ...DLG_STALLS,
];
